import React, { useEffect, useRef, useState } from 'react';
import { PipelineFlowDiagram } from './PipelineFlowDiagram';
import { LiveMetricChart } from './LiveMetricChart';
import { EvalScorecard } from './EvalScorecard';
import { StepStatsPanel } from './StepStatsPanel';
import { StepLogPanel } from './StepLogPanel';
import { subscribeToLeagueEvents } from './eventSubscriptionClient';
import { getActiveRun, getRunEvents, listLeagues, listPastRuns, ReadApiError } from './readApiClient';
import { PipelineEventEnvelope, RunIndexEntry, StepStatusByRole } from './interfaces';
import { applyEvent, applyEvents, DashboardState, emptyDashboardState, sortEventsChronologically } from './dashboardState';
import './theme.css';

// A sentinel <select> option value distinct from both the disabled ''
// placeholder and any real run_id -- lets onChange tell "user picked the
// 'back to live' option" apart from "user picked an actual run" without
// guessing based on run_id shape. No real run_id could ever collide with
// this (SageMaker execution names/ids don't contain '__').
const BACK_TO_LIVE_OPTION_VALUE = '__back_to_live__';

/** Derives one overall running/succeeded/failed badge from the known steps' statuses -- the mockup's per-card status pill. */
function overallStatus(stepStatuses: StepStatusByRole): 'running' | 'succeeded' | 'failed' | 'pending' {
  const statuses = Object.values(stepStatuses);
  if (statuses.some((status) => status === 'failed')) return 'failed';
  if (statuses.length === 0) return 'pending';
  if (statuses.every((status) => status === 'succeeded')) return 'succeeded';
  return 'running';
}

const STATUS_BADGE: Record<ReturnType<typeof overallStatus>, { className: string; label: string }> = {
  running: { className: 'running', label: 'Running' },
  succeeded: { className: 'success', label: 'Succeeded' },
  failed: { className: 'failed', label: 'Failed' },
  pending: { className: 'running', label: 'Waiting for first event' },
};

/**
 * Root dashboard component: single-league live flow diagram, live metric
 * chart, eval scorecard, per-step log panels, and past-run view, styled to
 * match the approved design mockup (smartwater-mockup-live.html). No
 * league picker -- per explicit user direction, multi-league selection is
 * deferred until more than one league actually exists; today this
 * auto-discovers and shows whichever single league is registered (matches
 * the "add a league, zero frontend changes" acceptance criterion: this
 * component doesn't hardcode 'nfl', it still calls the same
 * league-discovery route).
 *
 * Live state is fed by a WebSocket subscription to the discovered
 * league's AppSync Events channel -- see
 * specs/done/smartwater-realtime-pipeline-dashboard.md's Approach item 8.
 * On mount (or when a new run starts), state is additionally backfilled
 * from GET /runs/{runId}/events so a page load or refresh mid-run shows
 * full history immediately rather than waiting on the next live event --
 * see specs/open/smartwater-granular-event-history.md's Approach item 5.
 */
export function Dashboard() {
  const [league, setLeague] = useState<string | null>(null);
  const [leagueError, setLeagueError] = useState<string | null>(null);
  // Distinguishes "haven't fetched leagues yet" from "fetched, found none" --
  // both leave `league` at its initial null, so without this flag the
  // Loading and empty-state messages would render simultaneously on first
  // paint, and Loading would persist forever once the fetch resolves to an
  // empty list.
  const [leaguesLoading, setLeaguesLoading] = useState(true);
  // The live run's own state -- always kept up to date by the WebSocket
  // subscription below regardless of what's currently displayed (see
  // viewedRunId's docstring for why live ingestion and display are
  // deliberately decoupled).
  const [liveState, setLiveState] = useState<DashboardState>(emptyDashboardState());
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null);
  // The run-selector dropdown's own list of runs for this league
  // (most-recent-first, per GET /runs?league= -- see that route's
  // ScanIndexForward docstring), refreshed on league change and whenever
  // the live run's overall status changes (so a run that just finished
  // appears in the dropdown without requiring a manual reload).
  const [pastRuns, setPastRuns] = useState<RunIndexEntry[]>([]);
  // null = viewing the live run (default). Set to a specific run_id when
  // the user picks a PAST (non-live) run from the dropdown -- per
  // explicit UX direction, replacing the old separate "Past Runs"
  // split-screen card: selecting a past run must populate the SAME main
  // UI (flow diagram, log panels, metric chart, scorecard) a live run
  // uses, not a separate bespoke view. Live ingestion (liveState) keeps
  // updating in the background while a past run is being viewed, so
  // switching back to the live run is instant and current.
  const [viewedRunId, setViewedRunId] = useState<string | null>(null);
  // The frozen event-history snapshot for viewedRunId, fetched once via
  // GET /runs/{runId}/events when the user selects a past run. null while
  // viewing the live run (liveState is used directly instead) or while a
  // past run's history is still loading.
  const [viewedRunState, setViewedRunState] = useState<DashboardState | null>(null);
  const [viewedRunError, setViewedRunError] = useState<string | null>(null);
  // The step whose log panel is shown, once the user clicks a node in the
  // flow diagram -- overrides the default "show whichever step is
  // currently active" behavior below, so clicking a FINISHED step's node
  // keeps its logs visible instead of the panel disappearing the instant
  // that step succeeds (a real reported UX gap: the log data was never
  // lost -- state.stepLogs keeps every step's lines for the run's
  // lifetime -- only the panel that rendered it was unmounting). Reset to
  // null (back to auto-follow-active) whenever a new run starts, so an
  // old run's pinned step doesn't carry over and silently show stale data
  // for the new run.
  const [pinnedStepRole, setPinnedStepRole] = useState<string | null>(null);
  // Tracks event_ids already applied to state -- the consumer Lambda's
  // AppSync-publish and DynamoDB-update are not atomic (see
  // consumerLambda/index.ts), so a transient failure on one side after the
  // other already succeeded causes Lambda's Kinesis retry to re-publish an
  // event that already reached this subscriber. Deduping here (rather than
  // trying to make the backend calls atomic) also covers any future
  // redelivery from AppSync's own retry semantics, not just this one
  // Lambda's failure mode. A plain mutable Set in a ref, not state --
  // membership tracking doesn't need to trigger a re-render.
  const seenEventIds = useRef<Set<string>>(new Set());

  // Tracks the run_id this dashboard's per-run state currently reflects. A
  // new run starting while this page stays open on the same league does
  // NOT re-trigger the subscribe effect below (its dependency is only
  // `league`) -- without this, a new run's first events would layer on top
  // of the previous run's stale display instead of starting fresh (e.g. a
  // step this new run hasn't reached yet would still show the OLD run's
  // succeeded/failed status). null until the first event or seed arrives.
  const currentRunId = useRef<string | null>(null);

  // Buffer-then-replay-then-drain for the backfill/live race: while a
  // run's history fetch (fetchAndReplayHistory) is in flight, live events
  // for that run are queued here instead of applied directly. Without
  // this, a live step_status arriving mid-fetch could be overwritten by
  // an OLDER replayed status for the same step once the fetch resolves
  // (different event_ids, so seenEventIds's dedup can't catch it). See
  // specs/open/smartwater-granular-event-history.md's Approach item 5.
  const backfillInFlight = useRef(false);
  const bufferedLiveEvents = useRef<PipelineEventEnvelope[]>([]);
  // Tracks whether the user has EXPLICITLY picked a run from the dropdown
  // this league-session -- distinguishes "still on the default (follow
  // whichever run is live)" from "user picked the run that happens to
  // currently be the live one." Without this, a new run starting while
  // the user is deliberately viewing the (now-finished) previous live run
  // would silently yank them back to the new run; this ref makes that
  // auto-follow strictly opt-in (the default, until a click happens).
  const userHasPickedARun = useRef(false);

  // Tracks the most recently REQUESTED viewed run_id (past run or a shared
  // URL's run) -- guards against a stale, slower fetch overwriting a
  // newer selection's data. selectRun and the shared-URL load below are
  // both async with no other ordering guarantee: if a user rapidly picks
  // run A then run B (or a URL-based fetch races a dropdown selection),
  // both fetches run concurrently, and if A's resolves AFTER B's,
  // setViewedRunState would clobber B's correct data with A's stale
  // data -- viewedRunId would still (correctly) say B, but the displayed
  // state would silently be A's. Set right before each fetch starts;
  // checked before every setViewedRunState/setViewedRunError call so a
  // superseded fetch's result is discarded instead of applied.
  const latestViewedRunRequest = useRef<string | null>(null);

  // Captured once on module/component load, before any effect can clear
  // it -- the run_id (if any) a shared/bookmarked URL's `?run=` query
  // param named. Consumed exactly once, by the league-discovery effect
  // below, to seed viewedRunId via the same selectRun() path a manual
  // dropdown click uses -- see that effect for why this can't just be
  // read directly into useState's initializer (selectRun's fetch-and-
  // freeze logic needs `league`/currentRunId to already be resolved).
  const runIdFromUrlOnLoad = useRef<string | null>(new URLSearchParams(window.location.search).get('run'));

  /**
   * Keeps the URL's `?run=` query param in sync with what's actually
   * displayed, so the current view is always a shareable/bookmarkable
   * link -- per explicit user direction. Uses replaceState (not
   * pushState) so browsing between runs via the dropdown doesn't spam
   * the browser's back-button history with one entry per selection;
   * `run=null` removes the param entirely (the live view's canonical URL
   * has no `?run=` at all, matching a fresh, never-shared visit).
   */
  function syncRunIdToUrl(runId: string | null): void {
    const url = new URL(window.location.href);
    if (runId) {
      url.searchParams.set('run', runId);
    } else {
      url.searchParams.delete('run');
    }
    window.history.replaceState(null, '', url);
  }

  useEffect(() => {
    listLeagues()
      .then((response) => setLeague(response.leagues[0] ?? null))
      .catch((err: ReadApiError) => setLeagueError(err.message))
      .finally(() => setLeaguesLoading(false));
  }, []);

  useEffect(() => {
    if (!league) return;

    setLiveState(emptyDashboardState());
    setSubscriptionError(null);
    setPinnedStepRole(null);
    setViewedRunId(null);
    setViewedRunState(null);
    setViewedRunError(null);
    setPastRuns([]);
    seenEventIds.current = new Set();
    currentRunId.current = null;
    backfillInFlight.current = false;
    bufferedLiveEvents.current = [];
    userHasPickedARun.current = false;
    latestViewedRunRequest.current = null;

    /** Refreshes the run-selector dropdown's list -- most-recent-first, per listPastRuns's own ordering. */
    function refreshPastRuns(): void {
      listPastRuns(league!)
        .then((response) => setPastRuns(response.runs))
        .catch((err: ReadApiError) => setSubscriptionError(err.message));
    }
    refreshPastRuns();

    /**
     * Fetches a run's full event history (paginating through every page)
     * and replays it into state, then drains any live events buffered
     * while the fetch was in flight -- see backfillInFlight's docstring
     * for why buffering (not applying live events directly) during the
     * fetch is required for correctness, not just a nice-to-have.
     */
    async function fetchAndReplayHistory(runId: string): Promise<void> {
      backfillInFlight.current = true;
      try {
        const allEvents: PipelineEventEnvelope[] = [];
        let nextToken: string | undefined;
        do {
          const response = await getRunEvents(runId, { nextToken });
          allEvents.push(...response.events);
          nextToken = response.nextToken ?? undefined;
        } while (nextToken);

        // A live event may have already advanced currentRunId to a newer
        // run while this fetch was in flight -- don't clobber that newer
        // state with this now-stale fetch's results.
        if (currentRunId.current !== runId) return;

        const sorted = sortEventsChronologically(allEvents);
        for (const event of sorted) seenEventIds.current.add(event.event_id);
        setLiveState((previous) => applyEvents(previous, sorted));
      } finally {
        backfillInFlight.current = false;
        const buffered = bufferedLiveEvents.current;
        bufferedLiveEvents.current = [];
        if (buffered.length > 0) {
          setLiveState((previous) => applyEvents(previous, buffered));
        }
      }
    }

    // Seed with the run's current state (if one is in progress) before the
    // live subscription's first event arrives -- without this, a browser
    // that loads or refreshes mid-run shows nothing until the NEXT event
    // fires. Backfills full event history (not just the latest-state
    // step_statuses summary) so log lines and metrics are populated
    // immediately too, per the spec's acceptance criterion 4.
    let cancelled = false;
    getActiveRun(league)
      .then((activeRun) => {
        if (cancelled || !activeRun) return;
        if (currentRunId.current !== null && currentRunId.current !== activeRun.run_id) return;
        currentRunId.current = activeRun.run_id;
        return fetchAndReplayHistory(activeRun.run_id);
      })
      .catch((err: ReadApiError) => setSubscriptionError(err.message));

    // A shared/bookmarked URL's `?run=` param, if present, is loaded
    // exactly once per mount (runIdFromUrlOnLoad.current is cleared right
    // after) -- fetches and freezes that run's full history into
    // viewedRunState, same as a manual dropdown pick. Always fetches
    // (does not special-case "this happens to be the live run") --
    // currentRunId.current is not yet resolved at this point in the
    // effect (getActiveRun above is still in flight), so there is no
    // reliable way to know that here; a redundant fetch for what turns
    // out to be the live run is harmless (same data liveState will also
    // have), just not maximally efficient.
    if (runIdFromUrlOnLoad.current) {
      const sharedRunId = runIdFromUrlOnLoad.current;
      runIdFromUrlOnLoad.current = null;
      userHasPickedARun.current = true;
      setViewedRunId(sharedRunId);
      latestViewedRunRequest.current = sharedRunId;
      getRunEvents(sharedRunId)
        .then(async (firstPage) => {
          const allEvents = [...firstPage.events];
          let nextToken = firstPage.nextToken ?? undefined;
          while (nextToken) {
            const page = await getRunEvents(sharedRunId, { nextToken });
            allEvents.push(...page.events);
            nextToken = page.nextToken ?? undefined;
          }
          // Guard: a newer selection may have superseded this fetch while
          // it was in flight -- see latestViewedRunRequest's docstring.
          if (latestViewedRunRequest.current !== sharedRunId) return;
          setViewedRunState(applyEvents(emptyDashboardState(), sortEventsChronologically(allEvents)));
        })
        .catch((err: ReadApiError) => {
          if (latestViewedRunRequest.current !== sharedRunId) return;
          setViewedRunError(err.message);
        });
    }

    const unsubscribe = subscribeToLeagueEvents(
      league,
      (event: PipelineEventEnvelope) => {
        if (seenEventIds.current.has(event.event_id)) {
          return; // Already applied -- a redelivered event, not a new one.
        }
        seenEventIds.current.add(event.event_id);

        // A new run_id means a new pipeline execution started -- reset
        // every per-run display before applying this event, so the new
        // run starts from a clean slate instead of layering onto the
        // previous run's leftover state (see currentRunId's docstring).
        // Refreshes the dropdown too, so the just-finished previous run
        // (and the brand-new one) both show up without a manual reload.
        if (currentRunId.current !== null && currentRunId.current !== event.run_id) {
          setLiveState(emptyDashboardState());
          bufferedLiveEvents.current = [];
          refreshPastRuns();
          // Only auto-follow the new live run if the user never
          // explicitly picked a different run to view -- see
          // userHasPickedARun's docstring.
          if (!userHasPickedARun.current) {
            setPinnedStepRole(null);
            setViewedRunId(null);
            setViewedRunState(null);
          }
        }
        currentRunId.current = event.run_id;

        if (backfillInFlight.current) {
          bufferedLiveEvents.current.push(event);
          return;
        }
        setLiveState((previous) => applyEvent(previous, event));
      },
      (error: Error) => setSubscriptionError(error.message),
    );

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [league]);

  // Refreshes the dropdown when the LIVE run reaches a terminal status
  // without a new run having started yet -- the "new run_id arrived"
  // branch above only refreshes on the NEXT run's first event, which
  // could be minutes away (or never, if this was the last run of the
  // day). This effect catches that gap so a just-finished run's dropdown
  // entry updates (e.g. its highlight/label) promptly.
  const liveOverallStatus = overallStatus(liveState.stepStatuses);
  useEffect(() => {
    if (!league) return;
    if (liveOverallStatus !== 'succeeded' && liveOverallStatus !== 'failed') return;
    listPastRuns(league)
      .then((response) => setPastRuns(response.runs))
      .catch(() => undefined); // Best-effort refresh; the dropdown just stays stale until the next successful refresh.
  }, [league, liveOverallStatus]);

  /**
   * Handles a run-selector dropdown pick. Selecting the live run (or
   * `null`) switches display back to liveState with no fetch needed --
   * it's already being kept current in the background. Selecting any
   * other (necessarily past/finished) run fetches its full event history
   * once and freezes it in viewedRunState, matching the same
   * fetch-and-paginate pattern fetchAndReplayHistory uses for the live
   * run's own backfill.
   */
  async function selectRun(runId: string): Promise<void> {
    userHasPickedARun.current = true;
    syncRunIdToUrl(runId);
    setPinnedStepRole(null);
    setViewedRunId(runId);
    setViewedRunError(null);
    latestViewedRunRequest.current = runId;
    // Selecting the run that's already live needs no fetch -- liveState
    // already has its full history (backfilled on mount/run-start) and
    // keeps updating in real time. Reported live, 2026-08-05: the
    // dropdown's value must still become `runId` here (not reset to the
    // placeholder) even though no fetch happens, or re-selecting this
    // exact option from the placeholder state would show no visible
    // change and look broken.
    if (runId === currentRunId.current) {
      setViewedRunState(null);
      return;
    }
    setViewedRunState(null);
    try {
      const allEvents: PipelineEventEnvelope[] = [];
      let nextToken: string | undefined;
      do {
        const response = await getRunEvents(runId, { nextToken });
        allEvents.push(...response.events);
        nextToken = response.nextToken ?? undefined;
      } while (nextToken);
      // Guard: a newer selection may have superseded this fetch while it
      // was in flight -- see latestViewedRunRequest's docstring.
      if (latestViewedRunRequest.current !== runId) return;
      setViewedRunState(applyEvents(emptyDashboardState(), sortEventsChronologically(allEvents)));
    } catch (err) {
      if (latestViewedRunRequest.current !== runId) return;
      setViewedRunError(err instanceof ReadApiError ? err.message : `${err}`);
    }
  }

  /**
   * Returns to the default, always-current live view. Distinct from
   * selectRun(currentRunId.current) -- that still marks the LIVE run as
   * the user's explicit pick (userHasPickedARun stays true, so a future
   * new run won't auto-follow); this instead reverts to the true
   * un-picked default (userHasPickedARun back to false), matching a
   * fresh page load exactly. Per explicit user direction: the dropdown
   * needs a real way back to the entry point once a past run has been
   * selected, not just a way to re-view whichever run happens to be live
   * right now.
   */
  function resetToLive(): void {
    userHasPickedARun.current = false;
    syncRunIdToUrl(null);
    setPinnedStepRole(null);
    setViewedRunId(null);
    setViewedRunState(null);
    setViewedRunError(null);
    latestViewedRunRequest.current = null;
  }

  // Whichever state is actually on screen. viewedRunId === currentRunId.current
  // means the user explicitly picked the live run (or it's still the
  // untouched default) -- either way liveState is used directly, since
  // it's always current and there's nothing to freeze. Any OTHER
  // viewedRunId means a genuinely past run was picked: viewedRunState
  // (a frozen snapshot, fetched once) is used once its fetch resolves,
  // falling back to liveState only transiently while that fetch is in
  // flight (see loading-state rendering below for the user-visible cue
  // during that window, so this fallback is never mistaken for the past
  // run's real data).
  const isViewingLiveRun = viewedRunId === null || viewedRunId === currentRunId.current;
  const state = isViewingLiveRun ? liveState : viewedRunState ?? liveState;

  // The dropdown's actual option list -- pastRuns (from GET /runs?league=)
  // PLUS a synthetic entry for the current live run if it isn't in there
  // yet. A brand-new run's RunIndexTable row is written by the consumer
  // Lambda asynchronously as its events arrive, so there's a real window
  // (this run's first few seconds) where currentRunId is already known
  // client-side but pastRuns's last fetch predates it -- without this
  // fallback the just-started run would be briefly missing from its own
  // dropdown.
  const dropdownRuns: RunIndexEntry[] =
    currentRunId.current && !pastRuns.some((run) => run.run_id === currentRunId.current)
      ? [{ run_id: currentRunId.current, league: league ?? '', run_status: 'running' }, ...pastRuns]
      : pastRuns;

  const badge = STATUS_BADGE[overallStatus(state.stepStatuses)];
  // Every step_role that has had ANY activity so far this run (status or
  // log line), in first-seen order (state.stepStatuses/stepLogs are only
  // ever updated additively via `{...previous, [role]: ...}`, so JS's
  // insertion-order guarantee for string keys gives arrival order for
  // free -- no extra bookkeeping needed). This is the default: a user
  // landing on the dashboard -- live or via page reload -- sees every
  // step's panel that's run so far, not just whichever one happens to be
  // active at that instant.
  //
  // Regression context, 2026-08-05: an earlier version of this default
  // showed ONLY activeStepRoles, so a reload landing on step 3 (with
  // steps 1-2 already succeeded) showed nothing for steps 1-2 at all --
  // their log data was never lost (state.stepLogs retains it for the
  // run's lifetime), only the default selection excluded them. A
  // narrower attempted fix (falling back to just the SINGLE
  // most-recently-active step during the inter-step gap) was tried and
  // rejected for the same reason: it still hid step 1 when the user was
  // looking at step 3, not just during the brief gap between 2 and 3.
  // Union of stepStatuses and stepLogs keys (not stepStatuses alone) --
  // a step_log can arrive before that step's first step_status (e.g. a
  // script's very first emit_log call, before its
  // emit_step_status("in_progress")), so deriving from stepStatuses
  // alone would miss it.
  const stepRolesSeenSoFar = Array.from(new Set([...Object.keys(state.stepStatuses), ...Object.keys(state.stepLogs)]));
  // A user-clicked step (pinnedStepRole) narrows the view to just that
  // one step -- see pinnedStepRole's own docstring for why a click
  // should isolate a single step's panel rather than add to the list.
  const displayedStepRoles = pinnedStepRole ? [pinnedStepRole] : stepRolesSeenSoFar;

  return (
    <>
      <header className="sw-header">
        <div>
          <h1>SmartWater Pipeline Status</h1>
          <div className="subtitle">smartwater.pv.amazon.dev</div>
        </div>
        <div className="live-pill">
          <span className="dot" />
          LIVE &mdash; streaming
        </div>
      </header>

      <main className="sw-main">
        {leagueError && <p style={{ color: '#fca5a5' }}>Failed to load leagues: {leagueError}</p>}
        {!leagueError && leaguesLoading && <p style={{ color: 'var(--text-dim)' }}>Loading&hellip;</p>}
        {!leagueError && !leaguesLoading && league === null && <p>No league has a registered pipeline yet.</p>}

        {league && (
          <div className="card">
            <div className="card-header">
              <div>
                <div className="league-name">{league.toUpperCase()}</div>
                <div className="league-sub">Training Pipeline</div>
              </div>
              {/* Run-selector dropdown: replaces the old separate "Past
                  Runs" split-screen card -- per explicit UX direction,
                  picking a past run must populate this SAME main UI
                  (flow diagram, log panels, metric chart, scorecard),
                  not a separate bespoke view. The currently-live run (if
                  any) is visually distinguished with a bullet so it
                  reads apart from finished runs at a glance. */}
              {dropdownRuns.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <select
                    aria-label="Select a pipeline run to view"
                    // A real, distinct placeholder value (not the live
                    // run's own run_id) when nothing has been explicitly
                    // picked yet -- reported live, 2026-08-05: defaulting
                    // this <select>'s value to the live run's own run_id
                    // meant the browser considered that option ALREADY
                    // selected, so clicking it again fired no onChange at
                    // all (the value didn't change from the DOM's
                    // perspective) -- the only way to "select" the live
                    // run was to click a DIFFERENT option first, then
                    // click back. The placeholder is always a distinct
                    // value, so selecting the live run (or any run) from
                    // this state always fires a real onChange.
                    value={viewedRunId ?? ''}
                    onChange={(event) =>
                      event.target.value === BACK_TO_LIVE_OPTION_VALUE
                        ? resetToLive()
                        : selectRun(event.target.value)
                    }
                    className={`run-selector${isViewingLiveRun ? ' viewing-live' : ''}`}
                  >
                    <option value="" disabled>
                      Select a previous run
                    </option>
                    {/* Only shown once a past (non-live) run is actually
                        being viewed -- per explicit user direction, once
                        a past run is selected there was previously no
                        way back to the default live-following entry
                        point without a full page reload. Omitted while
                        already on the live view since it would be a
                        redundant no-op option in that state. */}
                    {viewedRunId !== null && !isViewingLiveRun && (
                      <option value={BACK_TO_LIVE_OPTION_VALUE}>&larr; Back to live</option>
                    )}
                    {dropdownRuns.map((run) => (
                      <option key={run.run_id} value={run.run_id}>
                        {/* "Live" is determined by run_id === currentRunId.current
                            (the client's own verified-active run, seeded
                            from GET /runs?league=&active= and kept current
                            by the live WebSocket subscription) -- NOT by
                            this row's own run_status field. Confirmed
                            live, 2026-08-05: a run whose pipeline execution
                            actually finished hours earlier can have a
                            permanently stale run_status: "running" row in
                            RunIndexTable if its terminal run_status event
                            was ever lost (a real pre-existing data gap,
                            not something introduced by this dropdown) --
                            trusting that field directly mislabeled old
                            finished runs as "Live" in the dropdown. */}
                        {run.run_id === currentRunId.current ? '\u25cf Live \u2014 ' : ''}
                        {run.run_id}
                        {run.last_updated ? ` (${run.last_updated})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <span className={`badge ${badge.className}`}>
                <span className="dot" />
                {badge.label}
              </span>
            </div>

            {subscriptionError && (
              <p style={{ color: '#fca5a5', margin: '0 22px 12px' }}>Live updates unavailable: {subscriptionError}</p>
            )}
            {viewedRunError && (
              <p style={{ color: '#fca5a5', margin: '0 22px 12px' }}>Failed to load run: {viewedRunError}</p>
            )}
            {viewedRunId && !viewedRunState && !viewedRunError && (
              <p style={{ color: 'var(--text-dim)', margin: '0 22px 12px' }}>Loading run history&hellip;</p>
            )}

            <PipelineFlowDiagram stepStatuses={state.stepStatuses} onStepClick={setPinnedStepRole} />
            <LiveMetricChart points={state.metricPoints} />
            <div style={{ padding: '4px 22px 22px' }}>
              <StepStatsPanel stats={state.stepStats} />
            </div>
            <div style={{ padding: '4px 22px 22px' }}>
              <EvalScorecard result={state.evalResult} />
            </div>
            {/* One streaming log panel per displayed step -- either the
                step the user clicked (pinnedStepRole, which stays visible
                even after that step finishes -- the fix for "the log box
                disappears the instant a step succeeds"), or, by default,
                whichever step(s) are currently active. */}
            {displayedStepRoles.map((stepRole) => (
              <div key={stepRole} style={{ padding: '4px 22px 22px' }}>
                <StepLogPanel logs={state.stepLogs[stepRole] ?? []} stepRole={stepRole} />
              </div>
            ))}
          </div>
        )}
      </main>

      <footer className="sw-footer">SmartWater &middot; live flow diagram, metric chart, and scorecard are driven by real pipeline events</footer>
    </>
  );
}
